
import React, { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";

interface AsuntolainaCalculatorProps {
  loanAmount?: number;
  setLoanAmount?: (value: number) => void;
  interestRate?: number;
  setInterestRate?: (value: number) => void;
  loanTerm?: number;
  setLoanTerm?: (value: number) => void;
  monthlyPayment?: number;
}

export const AsuntolainaCalculator = ({
  loanAmount: externalLoanAmount,
  setLoanAmount: externalSetLoanAmount,
  interestRate: externalInterestRate,
  setInterestRate: externalSetInterestRate,
  loanTerm: externalLoanTerm,
  setLoanTerm: externalSetLoanTerm,
  monthlyPayment: externalMonthlyPayment,
}: AsuntolainaCalculatorProps) => {
  const [internalLoanAmount, setInternalLoanAmount] = useState(200000);
  const [internalInterestRate, setInternalInterestRate] = useState(2.5);
  const [internalLoanTerm, setInternalLoanTerm] = useState(25);

  const loanAmount = externalLoanAmount ?? internalLoanAmount;
  const setLoanAmount = externalSetLoanAmount ?? setInternalLoanAmount;
  const interestRate = externalInterestRate ?? internalInterestRate;
  const setInterestRate = externalSetInterestRate ?? setInternalInterestRate;
  const loanTerm = externalLoanTerm ?? internalLoanTerm;
  const setLoanTerm = externalSetLoanTerm ?? setInternalLoanTerm;

  const calculateMonthlyPayment = () => {
    const monthlyInterestRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    const x = Math.pow(1 + monthlyInterestRate, numberOfPayments);
    const monthlyPayment = (loanAmount * x * monthlyInterestRate) / (x - 1);
    return isNaN(monthlyPayment) ? 0 : Math.round(monthlyPayment);
  };

  const monthlyPayment = externalMonthlyPayment ?? calculateMonthlyPayment();
  const totalPaid = monthlyPayment * loanTerm * 12;
  const totalInterest = totalPaid - loanAmount;

  return (
    <Card className="border border-blue-100 shadow-sm">
      <CardContent className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* Loan amount slider */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label htmlFor="loanAmount" className="text-sm font-medium text-gray-700">
                  Lainasumma: {formatCurrency(loanAmount)}
                </label>
                <Input
                  id="loanAmount"
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-24 text-right"
                  min={10000}
                  max={1000000}
                  step={1000}
                />
              </div>
              <Slider
                value={[loanAmount]}
                onValueChange={(values) => setLoanAmount(values[0])}
                min={10000}
                max={1000000}
                step={1000}
                className="cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>10 000 €</span>
                <span>1 000 000 €</span>
              </div>
            </div>

            {/* Interest rate slider */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label htmlFor="interestRate" className="text-sm font-medium text-gray-700">
                  Korko: {interestRate.toFixed(2)} %
                </label>
                <Input
                  id="interestRate"
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-24 text-right"
                  min={0.5}
                  max={10}
                  step={0.1}
                />
              </div>
              <Slider
                value={[interestRate]}
                onValueChange={(values) => setInterestRate(values[0])}
                min={0.5}
                max={10}
                step={0.1}
                className="cursor-pointer"
              />
            </div>

            {/* Loan term slider */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label htmlFor="loanTerm" className="text-sm font-medium text-gray-700">
                  Laina-aika: {loanTerm} vuotta
                </label>
                <Input
                  id="loanTerm"
                  type="number"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                  className="w-24 text-right"
                  min={1}
                  max={35}
                  step={1}
                />
              </div>
              <Slider
                value={[loanTerm]}
                onValueChange={(values) => setLoanTerm(values[0])}
                min={1}
                max={35}
                step={1}
                className="cursor-pointer"
              />
            </div>
          </div>

          {/* Results */}
          <div className="bg-blue-50 p-5 rounded-lg">
            <h3 className="font-semibold text-lg text-blue-800 mb-4">Laskelman tulos</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-blue-200">
                <span className="text-gray-600">Kuukausierä:</span>
                <span className="font-semibold text-lg text-blue-700">
                  {formatCurrency(monthlyPayment)} €/kk
                </span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-blue-200">
                <span className="text-gray-600">Kokonaiskustannus:</span>
                <span className="font-medium text-blue-700">
                  {formatCurrency(totalPaid)} €
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Korkokulut yhteensä:</span>
                <span className="font-medium text-blue-700">
                  {formatCurrency(totalInterest)} €
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
